import { streamingFieldText } from "../../i18n/streamingFeedbackPresentation";
import { stText, useStandaloneLanguage } from "../../i18n/streamingPagesPresentation";
/**
 * UserAdminPanel -- SideFlyout-based admin panel for per-user Emby/Jellyfin management.
 *
 * Provides 4 tabs: Overview, Controls, Devices, History.
 * Uses intent-based settings mutations for upstream server changes and
 * WSD-local policy for enforcement bypass.
 */
import { useCallback, useState } from "react";

import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { useDialog } from "../ui/Dialog";
import { Icon } from "../ui/Icon";
import { SideFlyout } from "../ui/SideFlyout";
import { Spinner } from "../ui/Spinner";
import { Tooltip } from "../ui/Tooltip";
import { cn } from "../../utils/cn";
import { toast } from "../../lib/toast";
import { getServerIcon, getServerColorClass, getServerLabel } from "../../utils/serverDisplay";
import { useWSDSettings, type MediaServerType } from "../../hooks/useWSDSettings";
import {
  useUserProfile,
  useUserHistory,
  type HistoryWindow,
} from "../../hooks/useUserProfile";
import { useUserPolicy, useUpdateUserPolicy } from "../../hooks/useUserPolicy";
import { useUserDevices } from "../../hooks/useUserDevices";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useStopAllSessions } from "../../hooks/useUserSessionActions";
import { useWSDSessions } from "../../hooks/useWSDSessions";
import { OverviewTab } from "./user-admin/OverviewTab";
import { ControlsTab } from "./user-admin/ControlsTab";
import { DevicesTab } from "./user-admin/DevicesTab";
import { HistoryTab } from "./user-admin/HistoryTab";
import { TemplatesTab } from "./user-admin/TemplatesTab";
import { formatRelativeTime } from "./user-admin/helpers";

// ============================================================================
// Types
// ============================================================================

export type UserAdminPanelProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  serverType: string;
};

type TabId = "overview" | "controls" | "devices" | "history" | "templates";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", get label() { return stText("overview"); }, icon: "lucide:layout-dashboard" },
  { id: "controls", get label() { return stText("controls"); }, icon: "lucide:sliders-horizontal" },
  { id: "devices", get label() { return stText("devices"); }, icon: "lucide:monitor-smartphone" },
  { id: "history", get label() { return stText("history"); }, icon: "lucide:history" },
  { id: "templates", get label() { return stText("templates"); }, icon: "lucide:copy-check" },
];

// ============================================================================
// Component
// ============================================================================

export const UserAdminPanel = ({
  open,
  onOpenChange,
  userId,
  userName,
  serverType,
}: UserAdminPanelProps) => {
  useStandaloneLanguage();
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [historyWindow, setHistoryWindow] = useState<HistoryWindow>("30d");
  const [avatarError, setAvatarError] = useState(false);

  const { confirm } = useDialog();

  // -- Data hooks (enabled only when panel is open)
  const {
    profile,
    isLoading: profileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useUserProfile(serverType, userId, open);

  const {
    history,
    isLoading: historyLoading,
    error: historyError,
  } = useUserHistory(serverType, userId, historyWindow, open && activeTab === "history");

  const {
    policy,
    isLoading: policyLoading,
    refetch: refetchPolicy,
  } = useUserPolicy(serverType, userId, open);

  const {
    devices,
    isLoading: devicesLoading,
    error: devicesError,
    refetch: refetchDevices,
  } = useUserDevices(serverType, userId, open && activeTab === "devices");

  // -- Mutation hooks
  const { updating: settingsUpdating, updateSettings } = useUserSettings();
  const { updating: policyUpdating, updatePolicy } = useUpdateUserPolicy();
  const { settings: wsdSettings } = useWSDSettings();
  const { stopping, stopAll } = useStopAllSessions();

  // -- SSE sessions (for overview active session count)
  const { sessions } = useWSDSessions();

  // -- Handlers
  const handleStopAll = useCallback(async () => {
    const confirmed = await confirm({
 renderText: () => ({ title: stText("stopAllActiveSessions"), description: stText("stopAllActiveSessionsForUserNameThisWillImmediatelyEnd", { userName: userName }), confirmText: stText("stopAll") }),

      title: stText("stopAllActiveSessions"),
      description: stText("stopAllActiveSessionsForUserNameThisWillImmediatelyEnd", { userName: userName }),
      confirmText: stText("stopAll"),
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      const result = await stopAll(serverType, userId);
      if (result) {
        toast.success(
          stText(result.failed > 0 ? "stoppedSessionsWithFailures" : "stoppedSessions", { count: result.stopped, failed: result.failed })
        );
        refetchProfile();
      }
    } catch {
      toast.error(stText("failedToStopSessions"));
    }
  }, [serverType, userId, userName, confirm, stopAll, refetchProfile]);

  const handleUpdateSettings = useCallback(
    async (patch: Parameters<typeof updateSettings>[2]) => {
      await updateSettings(serverType, userId, patch);
    },
    [serverType, userId, updateSettings]
  );

  const handleUpdatePolicy = useCallback(
    async (update: Parameters<typeof updatePolicy>[2]) => {
      await updatePolicy(serverType, userId, update);
      refetchPolicy();
    },
    [serverType, userId, updatePolicy, refetchPolicy]
  );

  const handleToggleAllDevices = useCallback(
    async (enabled: boolean) => {
      await updateSettings(serverType, userId, { allDevices: enabled });
    },
    [serverType, userId, updateSettings]
  );

  const handleWindowChange = useCallback((w: HistoryWindow) => {
    setHistoryWindow(w);
  }, []);

  // Avatar URL
  const avatarUrl = profile?.avatarUrl ?? null;

  return (
    <SideFlyout
      open={open}
      onOpenChange={onOpenChange}
      title={stText("userNameUserAdmin", { userName: userName })}
      icon="lucide:shield-check"
      className="sm:w-[min(100vw,70%)]"
    >
      {/* Loading state */}
      {!profile && profileLoading && (
        <div className="px-5 py-12">
          <div className="flex items-center justify-center gap-3">
            <Spinner size="5" />
            <span className="text-sm text-muted-foreground">
              {stText("loadingUserProfile")}</span>
          </div>
        </div>
      )}

      {/* Error state */}
      {profileError && !profile && (
        <div className="px-5 py-6">
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3">
            <div className="flex items-center gap-3">
              <Icon icon="lucide:alert-triangle" className="h-5 w-5 text-destructive shrink-0" />
              <div>
                <p className="text-sm font-medium text-destructive-text">
                  {stText("failedToLoadUserProfile")}</p>
                <p className="text-[12px] text-destructive-text mt-0.5">
                  {profileError}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile loaded */}
      {profile && (
        <div className="flex flex-col h-full">
          {/* Profile Bar */}
          <div className="px-5 py-3 border-b border-border/30 shrink-0">
            <div className="flex items-center gap-3">
              {/* Avatar */}
              {avatarUrl && !avatarError ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="h-11 w-11 rounded-full object-cover bg-muted/30 flex-shrink-0"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <div className="flex h-11 w-11 rounded-full bg-primary/10 text-primary-text font-semibold text-base items-center justify-center flex-shrink-0">
                  {userName.charAt(0).toUpperCase()}
                </div>
              )}

              {/* Name + Badges */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground truncate">
                    {profile.userName}
                  </h3>
                  {profile.isAdmin && (
                    <Badge variant="primary" size="xs">{stText("admin")}</Badge>
                  )}
                  {profile.isDisabled && (
                    <Badge variant="danger" size="xs">{stText("disabled")}</Badge>
                  )}
                  {profile.isHidden && (
                    <Badge variant="muted" size="xs">{stText("hidden")}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <Icon
                    icon={getServerIcon(serverType)}
                    className={cn("h-3.5 w-3.5", getServerColorClass(serverType))}
                  />
                  <span className="text-[11px] text-muted-foreground capitalize">
                    {serverType}
                  </span>
                  <span className="text-border">|</span>
                  <Tooltip content={profile.lastActivityDate ?? stText("noRecentActivity")}>
                    <span className="text-[11px] text-muted-foreground cursor-help" tabIndex={0}>{stText("inline8", { value0: formatRelativeTime(profile.lastActivityDate) })}</span>
                  </Tooltip>
                </div>
              </div>

              {/* Stop All Sessions */}
              <Button
                variant="destructive"
                size="sm"
                className="shrink-0"
                onClick={handleStopAll}
                disabled={stopping}
              >
                {stopping ? (
                  <Spinner size="3" className="mr-1.5" />
                ) : (
                  <Icon icon="lucide:octagon" className="h-3.5 w-3.5 mr-1.5" />
                )}
                {stText("stopAll2")}</Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="px-5 border-b border-border/30 shrink-0">
            <div className="flex overflow-x-auto qb-scroll items-center gap-0.5 -mb-px">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex shrink-0 whitespace-nowrap items-center gap-1.5 px-3 py-2.5 text-[12px] font-medium transition-colors border-b-2",
                    activeTab === tab.id
                      ? "border-primary text-primary-text"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border/50"
                  )}
                >
                  <Icon icon={tab.icon} className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto qb-scroll px-5 py-4">
            {activeTab === "overview" && (
              <OverviewTab
                profile={profile}
                sessions={sessions}
              />
            )}

            {activeTab === "controls" && (
              <>
                {wsdSettings?.serverCapabilities?.[serverType as MediaServerType]?.userPolicy === false && (
                  <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                    <div className="flex items-start gap-2">
                      <Icon icon="lucide:info" className="h-3.5 w-3.5 mt-0.5 text-amber-400 flex-shrink-0" aria-hidden />
                      <span>{stText("inline9", { value0: getServerLabel(serverType), value1: getServerLabel(serverType) })}</span>
                    </div>
                  </div>
                )}
                <ControlsTab
                  profile={profile}
                  policy={policy}
                  policyLoading={policyLoading}
                  settingsUpdating={settingsUpdating}
                  policyUpdating={policyUpdating}
                  onUpdateSettings={handleUpdateSettings}
                  onUpdatePolicy={handleUpdatePolicy}
                  onRefreshProfile={refetchProfile}
                />
              </>
            )}

            {activeTab === "devices" && (
              <DevicesTab
                devices={devices}
                isLoading={devicesLoading}
                error={devicesError}
                serverType={serverType}
                username={userName}
                onRefetch={refetchDevices}
                enableAllDevices={profile.policy.enableAllDevices}
                settingsUpdating={settingsUpdating}
                onToggleAllDevices={handleToggleAllDevices}
              />
            )}

            {activeTab === "history" && (
              <HistoryTab
                history={history}
                historyLoading={historyLoading}
                historyError={historyError}
                historyWindow={historyWindow}
                onWindowChange={handleWindowChange}
              />
            )}

            {activeTab === "templates" && (
              <TemplatesTab
                userId={userId}
                userName={userName}
                serverType={serverType}
                onRefreshProfile={refetchProfile}
                availableLibraries={profile.availableLibraries ?? []}
              />
            )}
          </div>
        </div>
      )}
    </SideFlyout>
  );
};
